import React, { useState } from 'react';

import { ActionIcon, Box, Button, Group, InputBase, Popover, Text } from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { IconX } from '@tabler/icons-react';

type TimeRangePickerFilterProps = {
  className?: string;
  value: [string | null, string | null];
  onChange: (value: [string | null, string | null]) => void;
  label?: string;
  placeholder?: string;
};

const formatInput = (value: [string | null, string | null]): string => {
  if (!value[0] || !value[1]) return '';
  return `${value[0]} - ${value[1]}`;
};

const isValidTime = (time: string): boolean => {
  return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
};

const isTimeRangeValid = (fromTime: string, toTime: string): boolean => {
  if (!isValidTime(fromTime) || !isValidTime(toTime)) return false;

  const [fromHour, fromMinute] = fromTime.split(':').map(Number);
  const [toHour, toMinute] = toTime.split(':').map(Number);

  const fromTotalMinutes = fromHour * 60 + fromMinute;
  const toTotalMinutes = toHour * 60 + toMinute;

  return fromTotalMinutes < toTotalMinutes;
};

const TimeRangePickerFilter = ({
  className,
  value,
  onChange,
  label,
  placeholder,
}: TimeRangePickerFilterProps): JSX.Element => {
  const [open, setOpened] = useState(false);
  const [draft, setDraft] = useState<[string | null, string | null]>(value);
  const [error, setError] = useState('');

  const formatted = formatInput(value);
  const hasValue = !!(value[0] && value[1]);

  const handleUpdate = (): void => {
    const [fromTime, toTime] = draft;

    if (!fromTime || !toTime) {
      setError('Both start and end times are required');
      return;
    }

    if (!isValidTime(fromTime) || !isValidTime(toTime)) {
      setError('Please enter valid times in HH:MM format');
      return;
    }

    if (!isTimeRangeValid(fromTime, toTime)) {
      setError('Start time must be before end time');
      return;
    }

    setError('');
    onChange(draft);
    setOpened(false);
  };

  const handleTimeChange = (index: 0 | 1, newTime: string): void => {
    const newDraft: [string | null, string | null] = [...draft];
    newDraft[index] = newTime;
    setDraft(newDraft);
    setError('');
  };

  const handleClear = (): void => {
    setDraft([null, null]);
    onChange([null, null]);
    setError('');
    setOpened(false);
  };

  const clearButton = hasValue ? (
    <ActionIcon onClick={handleClear}>
      <IconX size={14} />
    </ActionIcon>
  ) : null;

  return (
    <Popover opened={open} onClose={() => setOpened(false)} withArrow>
      <Popover.Target>
        <InputBase
          className={className}
          label={label}
          value={formatted}
          placeholder={placeholder}
          readOnly
          onClick={() => setOpened(o => !o)}
          rightSection={clearButton}
        />
      </Popover.Target>
      <Popover.Dropdown style={{ maxWidth: '100%' }}>
        <Box>
          <Group
            align='flex-start'
            sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}
            mb='md'
          >
            <TimeInput
              label='From'
              placeholder={placeholder}
              value={draft[0] || ''}
              onChange={v => handleTimeChange(0, v.target.value)}
              error={error && (!draft[0] || !isValidTime(draft[0]))}
            />
            <TimeInput
              label='To'
              value={draft[1] || ''}
              onChange={v => handleTimeChange(1, v.target.value)}
              error={error && (!draft[1] || !isValidTime(draft[1]))}
            />
          </Group>
          {error && (
            <Text color='red' size='sm' mb='md'>
              {error}
            </Text>
          )}
          <Group sx={{ display: 'flex', justifyContent: 'flex-end' }} mt='md'>
            <Button variant='subtle' onClick={() => setOpened(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate}>Update</Button>
          </Group>
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
};

export default TimeRangePickerFilter;
